import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = true }: NavbarProps) {
  const [scrolled, setScrolled] = useState(!transparent);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    if (!transparent) return;
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [transparent]);

  const isLight = !transparent || scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl font-semibold whitespace-nowrap transition-colors duration-300 ${
            isLight ? 'text-foreground-950' : 'text-white'
          }`}
        >
          Kakobuy<span className="text-primary-500">Polska</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                : 'text-white/85 hover:text-white hover:bg-white/10'
            }`}
          >
            <i className="ri-home-4-line mr-1.5"></i>Strona główna
          </Link>
          <a
            href="#kategorie"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                : 'text-white/85 hover:text-white hover:bg-white/10'
            }`}
          >
            Kategorie
          </a>
          <Link
            to="/poradnik-kakobuy"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                : 'text-white/85 hover:text-white hover:bg-white/10'
            }`}
          >
            <i className="ri-book-open-line mr-1.5"></i>Poradnik zakupowy
          </Link>
          <a
            href="#bezpieczenstwo"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                : 'text-white/85 hover:text-white hover:bg-white/10'
            }`}
          >
            Bezpieczeństwo
          </a>
          <a
            href="#faq"
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
              isLight
                ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                : 'text-white/85 hover:text-white hover:bg-white/10'
            }`}
          >
            FAQ
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-3 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Kakobuy Spreadsheet
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
            isLight ? 'text-foreground-950 hover:bg-background-100' : 'text-white hover:bg-white/10'
          }`}
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col px-4 py-3 gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-base"></i>
              Strona główna
            </Link>
            <Link
              to="/poradnik-kakobuy"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-base"></i>
              Poradnik zakupowy
            </Link>
            <a
              href="#kategorie"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              Kategorie produktów
            </a>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base"></i>
                Przeglądaj kategorie (14)
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="ml-2 border-l-2 border-secondary-200 pl-3 space-y-0.5 mt-1 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                    className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-foreground-600 hover:text-primary-600 hover:bg-background-50 rounded-md transition-colors cursor-pointer"
                  >
                    {cat.namePl}
                    <span className="text-foreground-400 text-[10px]">({cat.nameEn})</span>
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-2"></div>
            <a
              href="#bezpieczenstwo"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              Bezpieczeństwo
            </a>
            <a
              href="#faq"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              FAQ
            </a>
            <Link
              to="/poradnik-kakobuy/jak-zaczac"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              Jak zacząć
            </Link>
            <Link
              to="/poradnik-kakobuy/wysylka-polska"
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              Wysyłka do Polski
            </Link>

            <div className="border-t border-background-200 my-2"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white text-foreground-800 text-sm font-medium rounded-lg border border-background-300 hover:border-primary-300 transition-colors cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}